import React from 'react';
import styled from 'styled-components';

const AboutContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const MissionSection = styled.section`
  margin-bottom: 4rem;
  text-align: center;
`;

const MissionTitle = styled.h2`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1.5rem;
`;

const MissionText = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text};
  max-width: 800px;
  margin: 0 auto;
`;

const MainBioSection = styled.section`
  margin-bottom: 4rem;
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const BioHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 240px;
  gap: 3rem;
  margin-bottom: 2rem;
  align-items: center;
`;

const HeroBioHeader = styled.div`
  position: relative;
  margin-bottom: 2rem;
  border-radius: 15px;
  overflow: hidden;
  color: white;
  min-height: 500px;
  display: flex;
  align-items: center;

  h2, h3 {
    color: white;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
  }
`;

const HeroBioImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
`;

const HeroBioText = styled.div`
  position: absolute;
  z-index: 2;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 3rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.2) 80%, rgba(0,0,0,0) 100%);
`;

const BioImage = styled.img`
  width: calc(100% - 2rem);
  height: auto;
  border-radius: 10px;
  object-fit: cover;
  justify-self: start;
`;

const BioContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const BioName = styled.h2`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1rem;
`;

const BioTitle = styled.h3`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1rem;
  max-width: 650px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    max-width: 100%;
  }
`;

const BioText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1.5rem;
`;

const BioSection = styled.section`
  margin-bottom: 2rem;
`;

const BioSubtitle = styled.h3`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
  margin: 1.5rem 0 1rem;
`;

const BioList = styled.ul`
  list-style-type: disc;
  margin-left: 2rem;
  margin-bottom: 1rem;
`;

const BioListItem = styled.li`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0.5rem;
`;

const BioSubListItem = styled.li`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0.5rem;
  margin-left: 2rem;
  list-style-type: circle;
`;

const References = styled.section`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ReferencesTitle = styled.h3`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1rem;
`;

const ReferencesList = styled.ol`
  list-style-type: decimal;
  margin-left: 2rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
`;

const ReferenceItem = styled.li`
  margin-bottom: 0.5rem;
  a {
    color: ${({ theme }) => theme.colors.text};
    text-decoration: none;
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
      text-decoration: underline;
    }
  }
`;

const TeamSection = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 4rem;
`;

const TeamCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const TeamMemberImage = styled.img`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 1.5rem;
`;

const TeamMemberName = styled.h3`
  font-size: 1.8rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const TeamMemberTitle = styled.p`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1rem;
`;

const TeamMemberBio = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text};
`;

const ReferenceLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const CitationLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-size: 0.8em;
  vertical-align: super;
  &:hover {
    text-decoration: underline;
  }
`;

const CallToActionSection = styled.section`
  text-align: center;
  margin: 3rem 0 2rem;
  padding: 2rem;
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const CallToActionButton = styled.a`
  display: inline-block;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    transform: translateY(-1px);
  }
`;

const CallToActionText = styled.p`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1.5rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

function AboutPage() {
  return (
    <AboutContainer>
      <MissionSection>
        <MissionTitle>Our Mission</MissionTitle>
        <MissionText>
          To help companies push the envelope of plasma technologies by drawing on Dr. Yi's deep expertise in plasma engineering.
        </MissionText>
      </MissionSection>

      <MainBioSection>
        <HeroBioHeader>
          <HeroBioImage src="/images/dr-yi.webp" alt="Dr. Won Ju Yi" />
          <HeroBioText>
            <BioName>Won Ju Yi, Ph.D.</BioName>
            <BioTitle>Globally Recognized Expert in Plasma, Laser Technology, and Medical Aesthetics.</BioTitle>
          </HeroBioText>
        </HeroBioHeader>

        <BioContent>
          <BioSection>
             <BioText> <strong>Educational Background:</strong></BioText>
            <BioList>
              <BioListItem>B.S. in Physics from Inha University - 1983</BioListItem>
              <BioListItem>M.S. in Physics from Hampton University - 1987
              </BioListItem>
              <BioSubListItem>Thesis: "High Power UV Dye Laser Pumped by Hypocycloidal" </BioSubListItem>
              <BioListItem>Ph.D. in Electrical Engineering from the University of Nebraska-Lincoln - 1997</BioListItem>
              <BioSubListItem>Thesis: <a href="https://digitalcommons.unl.edu/elecengfacpub/67" target="_blank" rel="noopener noreferrer">"Study of Streamers Point-Plane Gap" </a></BioSubListItem>
</BioList>

          </BioSection>

          <BioSection>
            <BioSubtitle>Industry Leadership and Innovation</BioSubtitle>
            
            <BioText>With over 40 years of experience, Dr. Yi has specialized in high-voltage engineering, lasers, electro-
optics, cold plasma, semiconductor plasma,
plasma display panel (PDP) technologies, and various other plasma physics,engineering, and manufacturing applications.</BioText>
            <BioText>At
the University of Nebraska, he worked in Prof. P. F. Williams' lab on streamer discharges in nitrogen-
oxygen gas mixtures <CitationLink href="#ref1">[1]</CitationLink> and contributed to the development of variable pulse pico lasers for
dermatological treatments <CitationLink href="#ref2">[2]</CitationLink>. These efforts laid the groundwork for developing non-equilibrium (cold)
plasma and microplasma jet technologies applied in sterilization, material processing, and more.</BioText>
            <BioText>
              Early in his career, he led the development and commercialization of next-generation PDP technology at
Samsung Electronics. He later conducted active research on BNNT (Boron Nitride Nanotube) synthesis
and atmospheric-pressure microwave plasma torch technology at NASA, the U.S. Navy Research
Laboratory, Hampton University, coauthoring a paper on microwave-driven plasma materials <CitationLink href="#ref3">[3]</CitationLink>.</BioText>
<BioText>
              Recently, for nearly 7 years, Dr. Yi served as Chief Technology Officer (CTO) at Jeisys Medical Inc.,
overseeing R&amp;D for medical aesthetic skin devices including RF, HIFU, IPL, and laser technologies. He
successfully launched the needle RF device 'POTENZA' globally and played a key role in Jeisys Medical's
KOSDAQ listing in March 2021.</BioText>
          </BioSection>

          <BioSection>
            <BioSubtitle>Patents and Technological Contributions</BioSubtitle>
            
            <BioText>Dr. Yi has filed over 100 patents within his field of expertise, which can be viewed on <a href="/patents" target="_blank" rel="noopener noreferrer">our patents page</a>.</BioText>
            

            <BioText><strong>Recent Major Research Projects:</strong></BioText>
            
            <BioList>
              <BioListItem>Intelligent HIFU treatment system with real-time video guidance based on ICT
convergence (MSIT, 2022–2024)</BioListItem>
              <BioListItem>MR-guided high-intensity focused ultrasound system for brain and urogenital disorders
(MOTIE, 2017–2022)</BioListItem>
              <BioListItem>Medical pico-second laser with variable pulse widths and multi-pulse modes (MOTIE,
2020–2023)</BioListItem>
              <BioListItem>Research on BNNT synthesis using atmospheric-pressure plasma torches (U.S. Office of Naval Research,
2014–2017)</BioListItem>
            </BioList>
           
          </BioSection>
          <BioSection>
            <BioSubtitle>Present Day</BioSubtitle>
            <BioText>Currently, Dr. Yi focuses on low-temperature plasma research. Cold plasma, highlighted as a next-
generation core technology in the 2021 National Academies' Plasma Science report <CitationLink href="#ref4">[4]</CitationLink>, operates below
40°C and is gaining attention in eco-friendly air purification, medical aesthetics, and skin disease
treatment. Since the COVID-19 pandemic, cold plasma has been recognized as a promising method to
deactivate airborne viruses before inhalation. </BioText>
                <BioText>
He recently developed a novel dielectric barrier surface discharge plasma technology that operates at
low power consumption and produces no ozone odor, creating an eco-friendly plasma device. He also
designed portable plasma jet devices capable of using various gases such as helium, argon, and nitrogen,
overcoming previous challenges of high power consumption, ozone odor, and bulky size, thereby
advancing their commercialization and wider adoption.</BioText>
         
           
            <BioText>As the former CTO of major display manufacturing firms and a lead researcher at prestigious institutions, Dr. Yi has been at the forefront of plasma innovation for over three decades. He holds over 180 patents in plasma sources, thin-film deposition, and related fields. His work has been instrumental in developing next-generation displays and semiconductor processes. Now, he brings his wealth of knowledge to the wider field as a consultant and advisor. Through Core Plasma, Dr. Yi hopes to bring his decades of expertise to the next generation of plasma engineers.</BioText>
          </BioSection>

          <CallToActionSection>
            <CallToActionText>
              Ready to leverage Dr. Yi's expertise for your plasma technology projects?
            </CallToActionText>
            <CallToActionButton href="/contact">
              Get in Touch
            </CallToActionButton>
          </CallToActionSection>

          <References>
            <ReferencesTitle>References</ReferencesTitle>
            <ReferencesList>
              <ReferenceItem id="ref1">
                <a href="https://digitalcommons.unl.edu/elecengfacpub/67" target="_blank" rel="noopener noreferrer">
                  W. J. Yi & P. F. Williams, "Experimental study of streamers in pure N₂ and N₂/O₂ mixtures…", UNL Electrical Engineering Publications, 2002.
                </a>
              </ReferenceItem>
              <ReferenceItem id="ref2">
                <a href="https://patents.google.com/patent/US8207671B2" target="_blank" rel="noopener noreferrer">
                  U.S. Patent US8207671B2 – Plasma display panel with two discharge electrodes (Samsung SDI, filed 2008).
                </a>
              </ReferenceItem>
              <ReferenceItem id="ref3">
                <a href="https://onlinelibrary.wiley.com/doi/abs/10.1002/mop.10708" target="_blank" rel="noopener noreferrer">
                  K. D. Song et al., "Microwave Driven Thunder Materials", <em>Microwave and Optical Technology Letters</em>, Vol. 36, No. 5, 2003.
                </a>
              </ReferenceItem>
              <ReferenceItem id="ref4">
                <a href="https://doi.org/10.17226/25802" target="_blank" rel="noopener noreferrer">
                  National Academies of Sciences, Engineering, and Medicine. 2021. <em>Plasma Science: Enabling Technology, Sustainability, Security, and Exploration</em>. Washington, DC: The National Academies Press.
                </a>
              </ReferenceItem>
            </ReferencesList>
          </References>
        </BioContent>
      </MainBioSection>

      <MainBioSection>
        <BioHeader>
          <div>
            <BioName>Daniel Yi</BioName>
            <BioTitle>Head of Business Development</BioTitle>
          </div>
          <BioImage src="/images/daniel.webp" alt="Daniel Yi" />
        </BioHeader>

        <BioContent>
          <BioSection>
            <BioText>
              As a seasoned sales professional with a track record of success across diverse industries—from biotech to private capital—Daniel is the bridge between Core Plasma's deep scientific expertise and the companies that need it most. He leads business development, forging strategic partnerships and ensuring our clients' needs are met with precision and care. His role is to handle the business operations at Core Plasma and to connect cutting-edge plasma solutions with corporate R&D teams, ensuring Dr. Yi's expertise gets into the hands of those who can benefit from them most. He also happens to be Dr. Yi's son—which means this isn't just business, it's family legacy in motion.
            </BioText>
          </BioSection>
        </BioContent>
      </MainBioSection>

      <TeamSection>
        <TeamCard>
          <TeamMemberImage src="/images/dr-yi.webp" alt="Dr. Won Ju Yi" />
          <TeamMemberName>Won Ju Yi, Ph.D.</TeamMemberName>
          <TeamMemberTitle>Globally Recognized Expert in Plasma, Laser Technology, and Medical Aesthetics.</TeamMemberTitle>
          <TeamMemberBio>
            As the former CTO of major display manufacturing firms and a lead researcher at prestigious institutions, Dr. Yi has been at the forefront of plasma innovation for over three decades. He holds over 180 patents in plasma sources, thin-film deposition, and related fields. His work has been instrumental in developing next-generation displays and semiconductor processes. Now, he brings his wealth of knowledge to the wider field as a consultant and advisor. Through Core Plasma, Dr. Yi hopes to bring his decades of expertise to the next generation of plasma engineers.
          </TeamMemberBio>
        </TeamCard>
        <TeamCard>
          <TeamMemberImage src="/images/daniel.webp" alt="Daniel Yi" />
          <TeamMemberName>Daniel Yi</TeamMemberName>
          <TeamMemberTitle>Head of Business Development</TeamMemberTitle>
          <TeamMemberBio>
            As a seasoned sales professional with a track record of success across diverse industries—from biotech to private capital—Daniel is the bridge between Core Plasma's deep scientific expertise and the companies that need it most. He leads business development, forging strategic partnerships and ensuring our clients' needs are met with precision and care. His role is to handle the business operations at Core Plasma and to connect cutting-edge plasma solutions with corporate R&D teams, ensuring Dr. Yi's expertise gets into the hands of those who can benefit from them most. He also happens to be Dr. Yi's son—which means this isn't just business, it's family legacy in motion.
          </TeamMemberBio>
        </TeamCard>
      </TeamSection>
    </AboutContainer>
  );
}

export default AboutPage; 